import { CacheService } from './CacheService.js'
import { parseFrontmatter, renderMarkdown } from './MarkdownRenderer.js'

const DOCS_BASE_URL = import.meta.env.VITE_DOCS_URL || 'https://docs.haspdesigner.app'

export const DocumentationService = {
  baseUrl: DOCS_BASE_URL,

  setBaseUrl(url) {
    this.baseUrl = url.replace(/\/$/, '')
    CacheService.clear()
  },

  async fetchIndex() {
    const cacheKey = '__index__'
    if (CacheService.has(cacheKey)) return CacheService.get(cacheKey)

    const res = await fetch(`${this.baseUrl}/index.json`)
    if (!res.ok) throw new Error(`Failed to fetch docs index: ${res.status}`)
    const data = await res.json()
    CacheService.set(cacheKey, data)
    return data
  },

  async getDocument(id) {
    const cacheKey = `doc:${id}`
    if (CacheService.has(cacheKey)) return CacheService.get(cacheKey)

    const index = await this.fetchIndex()
    const entry = index.find(e => e.id === id)
    if (!entry) throw new Error(`Document not found: ${id}`)

    const url = entry.url.startsWith('http') ? entry.url : `${this.baseUrl}${entry.url}`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Failed to fetch document: ${res.status}`)

    const raw = await res.text()
    const { meta, content } = parseFrontmatter(raw)
    const html = this._rewriteAssetUrls(renderMarkdown(content))

    const doc = { ...entry, ...meta, content, html }
    CacheService.set(cacheKey, doc)
    return doc
  },

  async getByContext(context) {
    const index = await this.fetchIndex()
    const matches = index.filter(e => Array.isArray(e.context) && e.context.includes(context))
    if (matches.length === 0) return null
    return this.getDocument(matches[0].id)
  },

  async getNavigation() {
    const index = await this.fetchIndex()
    const categories = {}
    for (const entry of index) {
      const cat = entry.category || 'General'
      if (!categories[cat]) categories[cat] = []
      categories[cat].push(entry)
    }
    for (const cat of Object.values(categories)) {
      cat.sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
    }
    return categories
  },

  // Rewrite root-relative src/href attributes so images and links resolve
  // against the docs host, not the app origin.
  _rewriteAssetUrls(html) {
    return html.replace(/src="(\/[^"]+)"/g, (_, path) => `src="${this.baseUrl}${path}"`)
  },

  async prefetchCategory(category) {
    const index = await this.fetchIndex()
    const entries = index.filter(e => e.category === category)
    await Promise.allSettled(entries.map(e => this.getDocument(e.id)))
  },
}
